import { useController, useFormContext, Controller } from 'react-hook-form';
import { Box, Typography } from '@mui/material';
import { Info } from 'lucide-react';
import type { Dayjs } from 'dayjs';

import { djs } from '@/lib/formatters';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { TooltipContent } from '@/components/ui/tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import type { BoostMultiplier, Status } from '../../../models/campaign';
import DatePicker from '../../datepicker';
import PromotionNoEndToggle from '../PromotionNoEndToggle';
import { NO_END_DATE_TIMESTAMP } from '@/utils/constants';

interface Props {
  currentStartDate?: Dayjs | undefined;
  currentEndDate?: Dayjs | undefined;
  campaignStatus?: Status;
  isReadOnly?: boolean;
  multipliers: BoostMultiplier[];
}

export default function PromotionForm({
  currentStartDate,
  currentEndDate,
  campaignStatus,
  isReadOnly,
  multipliers,
}: Props) {
  const { control, formState, setError, setValue, watch } = useFormContext();
  const { errors } = formState;
  const { field: startDateField } = useController({
    name: 'startDate',
    control,
    rules: {
      required: 'Start date is required',
    },
  });

  const startDateDisabled = currentStartDate
    ? djs(currentStartDate).isBefore(djs(), 'day')
    : false;

  const endDateDisabled = currentEndDate
    ? djs(currentEndDate).isBefore(djs(), 'day')
    : false;

  const isNoEndDate = watch('isNoEndDate');
  const isCampaignEnded = campaignStatus === 'completed';
  const boostMultiplier = watch('boostMultiplier');
  const selectedMultiplier = multipliers.find(
    (m) => m.multiplier === boostMultiplier?.[0],
  );

  function handleNoEndDateChange(checked: boolean) {
    setValue('isNoEndDate', checked, { shouldValidate: true });
    if (checked) {
      setValue('endDate', djs(NO_END_DATE_TIMESTAMP), { shouldValidate: true });
    } else {
      setValue('endDate', undefined, { shouldValidate: true });
    }
  }

  return (
    <Box display="flex" flexDirection="column" gap="20px">
      <Box>
        <Typography
          variant="body2"
          component="label"
          display="flex"
          gap="2px"
          marginBottom="8px"
          color={isCampaignEnded ? 'text.disabled' : 'inherit'}
        >
          Name
          <Typography
            variant="body2"
            component="span"
            sx={{ color: 'error.main' }}
          >
            *
          </Typography>
        </Typography>
        <Controller
          name="name"
          control={control}
          rules={{
            required: 'Name is required',
          }}
          render={({ field }) => (
            <Input
              {...field}
              value={field.value ?? ''}
              type="text"
              disabled={isCampaignEnded || isReadOnly}
              className="w-full"
              data-testid="name-input"
              maxLength={255}
            />
          )}
        />
      </Box>
      <Box display="flex" gap="16px">
        <Box>
          <Controller
            name="startDate"
            control={control}
            rules={{
              required: 'Start date is required',
            }}
            render={({ field }) => (
              <>
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="flex-start"
                  gap="2px"
                  alignItems="center"
                  sx={{
                    margin: '0px',
                    padding: '0px',
                    marginBottom: '8px',
                  }}
                >
                  <Typography
                    variant="body2"
                    component="label"
                    display="flex"
                    gap="2px"
                    color={isCampaignEnded ? 'text.disabled' : 'inherit'}
                  >
                    Start date
                  </Typography>
                  <Typography
                    variant="body2"
                    component="span"
                    sx={{ color: 'error.main' }}
                  >
                    *
                  </Typography>
                </Box>
                <DatePicker
                  {...field}
                  disabled={startDateDisabled || isCampaignEnded || isReadOnly}
                  label=""
                  value={field.value ? djs(field.value) : null}
                  minDate={djs()}
                  errors={errors}
                  name="startDate"
                  onBlur={(event) => {
                    const newValue = event.target.value;
                    if (djs(newValue).isBefore(djs(), 'day')) {
                      setError('startDate', {
                        type: 'manual',
                        message: 'Invalid Start Date',
                      });
                    }
                  }}
                />
              </>
            )}
          />
        </Box>
        <Box>
          <Controller
            name="endDate"
            control={control}
            rules={{
              required: 'End date is required',
            }}
            render={({ field }) => (
              <>
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="flex-start"
                  gap="2px"
                  alignItems="center"
                  sx={{
                    margin: '0px',
                    padding: '0px',
                    marginBottom: '8px',
                  }}
                >
                  <Typography
                    variant="body2"
                    component="label"
                    display="flex"
                    gap="2px"
                    color={isCampaignEnded ? 'text.disabled' : 'inherit'}
                  >
                    End date
                  </Typography>
                  <Typography
                    variant="body2"
                    component="span"
                    sx={{
                      color: isCampaignEnded ? 'text.disabled' : 'error.main',
                    }}
                  >
                    *
                  </Typography>
                </Box>
                <DatePicker
                  {...field}
                  value={field.value && !isNoEndDate ? djs(field.value) : null}
                  style={{
                    display: isNoEndDate ? 'none' : 'flex',
                  }}
                  disabled={
                    endDateDisabled ||
                    isCampaignEnded ||
                    isNoEndDate ||
                    isReadOnly
                  }
                  placeholder={isNoEndDate ? '-' : 'MM/DD/YYYY'}
                  label=""
                  minDate={
                    djs(startDateField?.value).isAfter(djs(), 'day')
                      ? startDateField?.value
                      : djs()
                  }
                  errors={errors}
                  name="endDate"
                  onBlur={(event) => {
                    const newValue = event.target.value;
                    if (
                      djs(newValue).isBefore(djs(), 'day') ||
                      (startDateField?.value &&
                        djs(newValue).isBefore(startDateField?.value, 'day'))
                    ) {
                      setError('endDate', {
                        type: 'manual',
                        message: 'Invalid End Date',
                      });
                    }
                  }}
                />
                <PromotionNoEndToggle
                  control={control}
                  isCampaignEnded={isCampaignEnded}
                  handleNoEndDateChange={handleNoEndDateChange}
                  style={{
                    display: isNoEndDate ? 'flex' : 'none',
                    paddingTop: '7px',
                  }}
                  disabled={isReadOnly}
                />
              </>
            )}
          />
        </Box>
        {!isNoEndDate && (
          <PromotionNoEndToggle
            control={control}
            isCampaignEnded={isCampaignEnded}
            handleNoEndDateChange={handleNoEndDateChange}
            style={{
              display: 'flex',
            }}
            disabled={isReadOnly}
          />
        )}
      </Box>
      <div className="w-56">
        <Controller
          control={control}
          name="boostMultiplier"
          render={({ field }) => (
            <>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Label htmlFor="play-frequency">Play Frequency</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        className="h-6 w-6 p-0 hover:bg-transparent"
                        aria-label="Play Frequency information"
                      >
                        <Info className="text-primary h-4 w-4" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="z-9999 w-80" align="center">
                      <div className="space-y-2">
                        <h4 className="text-sm font-semibold">
                          Play Frequency
                        </h4>
                        <p className="text-muted-foreground text-sm">
                          Controls how often this creative appears compared to
                          others in the same playlist.
                        </p>
                        <div className="space-y-1.5 pt-2">
                          <div>
                            <p className="text-sm font-bold">Normal:</p>
                            <p className="text-muted-foreground text-sm">
                              Plays at the standard rate
                            </p>
                          </div>
                          <div>
                            <p className="text-sm font-bold">More:</p>
                            <p className="text-muted-foreground text-sm">
                              Plays more often than other creatives
                            </p>
                          </div>
                          <div>
                            <p className="text-sm font-bold">Max:</p>
                            <p className="text-muted-foreground text-sm">
                              Plays most frequently and will appear more often
                              than "Normal" or "More"
                            </p>
                          </div>
                        </div>
                        <p className="text-muted-foreground border-t pt-2 text-sm">
                          Your selection increases or decreases this creative's
                          share of total playback time.
                        </p>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
                {selectedMultiplier && (
                  <p className="text-muted-foreground text-sm capitalize">
                    {selectedMultiplier.name}
                  </p>
                )}
              </div>
              <div className="mt-3 w-56">
                <Slider
                  disabled={isCampaignEnded || isReadOnly}
                  id="play-frequency"
                  max={
                    multipliers.length > 0
                      ? multipliers[multipliers.length - 1].multiplier
                      : 2
                  }
                  min={1}
                  onValueChange={field.onChange}
                  tooltipContent={
                    selectedMultiplier ? (
                      <TooltipContent side="bottom">
                        {selectedMultiplier.multiplier}x
                      </TooltipContent>
                    ) : undefined
                  }
                  step={0.5}
                  value={[field.value]}
                />
              </div>
            </>
          )}
        />
      </div>
    </Box>
  );
}
